import { MINUTES_PER_DAY, MINUTES_PER_HOUR } from '@/time/clock';
import { type Weather, weatherGloom } from '@/time/weather';

/**
 * Time-of-day lighting as a single tinted overlay across the world.
 *
 * A full lighting pass is not worth it for a top-down pixel game: one
 * multiplied colour over the tilemap conveys dawn, noon, dusk and night
 * convincingly and costs one quad. Keyframes are interpolated so the change is
 * continuous rather than stepping at each hour.
 */

export interface Ambient {
  /** Overlay colour, 0xRRGGBB. */
  readonly colour: number;
  /** Overlay opacity, 0..1. */
  readonly alpha: number;
}

interface Keyframe {
  readonly hour: number;
  readonly colour: number;
  readonly alpha: number;
}

/**
 * Cool deep blue at night, warm amber at the edges of the day, nothing at noon.
 * The list must be sorted by hour and wraps from the last entry to the first.
 */
const KEYFRAMES: readonly Keyframe[] = [
  { hour: 0, colour: 0x14264a, alpha: 0.55 },
  { hour: 4, colour: 0x14264a, alpha: 0.52 },
  { hour: 6, colour: 0x6b4a7a, alpha: 0.34 },
  { hour: 7.5, colour: 0xffb36b, alpha: 0.14 },
  { hour: 10, colour: 0xffe9c4, alpha: 0.0 },
  { hour: 16, colour: 0xffe9c4, alpha: 0.0 },
  { hour: 18, colour: 0xff9a52, alpha: 0.16 },
  { hour: 19.5, colour: 0xc4643f, alpha: 0.3 },
  { hour: 21, colour: 0x2a3566, alpha: 0.45 },
  { hour: 23, colour: 0x14264a, alpha: 0.54 },
];

/** Interiors are lit by lamplight: dimmed at night, but never dark. */
const INDOOR_MAX_ALPHA = 0.22;
const INDOOR_COLOUR = 0x3b2f4a;

export interface AmbientOptions {
  readonly minuteOfDay: number;
  readonly weather: Weather;
  readonly indoor?: boolean;
}

export function ambientFor({ minuteOfDay, weather, indoor = false }: AmbientOptions): Ambient {
  const hour = wrapHour(minuteOfDay);
  const base = sampleKeyframes(hour);
  const gloom = weatherGloom(weather);

  if (indoor) {
    // Indoors the sky is irrelevant; only how late it is matters, and only a
    // little. Weather is ignored entirely — rain does not darken a lit room.
    return {
      colour: INDOOR_COLOUR,
      alpha: Math.min(base.alpha, 1) * INDOOR_MAX_ALPHA,
    };
  }

  return {
    colour: base.colour,
    // Gloom stacks on top of the hour so a storm at noon is still dim, but the
    // total is capped short of opaque — the player must always see the world.
    alpha: Math.min(0.78, base.alpha + gloom),
  };
}

function sampleKeyframes(hour: number): Ambient {
  let previous = KEYFRAMES[KEYFRAMES.length - 1]!;
  let next = KEYFRAMES[0]!;

  for (let i = 0; i < KEYFRAMES.length; i++) {
    const frame = KEYFRAMES[i]!;
    if (frame.hour > hour) {
      next = frame;
      previous = KEYFRAMES[(i - 1 + KEYFRAMES.length) % KEYFRAMES.length]!;
      break;
    }
    if (i === KEYFRAMES.length - 1) {
      previous = frame;
      next = KEYFRAMES[0]!;
    }
  }

  // Spans that cross midnight have a negative raw width; wrap it.
  let span = next.hour - previous.hour;
  if (span <= 0) span += 24;
  let offset = hour - previous.hour;
  if (offset < 0) offset += 24;

  const t = span === 0 ? 0 : clamp01(offset / span);

  return {
    colour: mixColour(previous.colour, next.colour, t),
    alpha: previous.alpha + (next.alpha - previous.alpha) * t,
  };
}

export function mixColour(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;

  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);

  return (r << 16) | (g << 8) | bl;
}

function wrapHour(minuteOfDay: number): number {
  const wrapped = ((minuteOfDay % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return wrapped / MINUTES_PER_HOUR;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
